import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { inviteUser } from '../../store/slices/authSlice';
import { fetchRoles } from '../../store/slices/rolesSlice';
import Dialog from '../common/Dialog';
import { showToast } from '../utils/Toast';
import LoadingSpinner from '../common/LoadingSpinner';

const AddUserDialog = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);
  const { roles, loading: rolesLoading } = useSelector((state) => state.roles);
  
  const [formData, setFormData] = useState({
    email: '',
    profile: {
      firstName: '',
      lastName: '',
      phone: '',
      role: 'project_manager', // Default role
      company: {
        name: '',
        position: '',
        department: ''
      }
    }
  });

  // Crew roles are no longer supported via settings
  const HIDDEN_ROLES = ['crew_leader', 'crew_member'];
  const visibleRoles = (roles || []).filter(role => !HIDDEN_ROLES.includes(role.value));

  const [errors, setErrors] = useState({});

  const inputClasses = `
    w-full border border-gray-200 px-4 py-2.5 rounded-md
    focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-900
    bg-white transition-colors
  `;

  const selectClasses = `
    w-full border border-gray-200 px-4 py-2.5 rounded-md
    focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-900
    bg-white transition-colors
  `;

  const labelClasses = "block text-sm font-medium text-gray-700 mb-1";

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.profile.firstName) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.profile.lastName) {
      newErrors.lastName = 'Last name is required';
    }

    if (formData.profile.phone && !/^\+?[\d\s-]+$/.test(formData.profile.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 3) { // For company fields
        setFormData(prev => ({
          ...prev,
          profile: {
            ...prev.profile,
            company: {
              ...prev.profile.company,
              [parts[2]]: value
            }
          }
        }));
      } else if (parts.length === 2) { // For other profile fields
        setFormData(prev => ({
          ...prev,
          profile: {
            ...prev.profile,
            [parts[1]]: value
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      const errorMessages = Object.values(errors);
      if (errorMessages.length > 0) {
        errorMessages.forEach(error => {
          showToast(error, 'error');
        });
      }
      return;
    }

    try {
      const result = await dispatch(inviteUser(formData)).unwrap();
      showToast(result.message || 'User invited successfully. An email has been sent with password setup instructions.', 'success');
      onSuccess();
      onClose();
    } catch (err) {
      showToast(err, 'error');
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">Add New Team Member</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className={labelClasses}>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`${inputClasses} ${errors.email ? 'border-red-500 ring-1 ring-red-500' : ''}`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>First Name</label>
              <input
                type="text"
                name="profile.firstName"
                value={formData.profile.firstName}
                onChange={handleChange}
                className={`${inputClasses} ${errors.firstName ? 'border-red-500 ring-1 ring-red-500' : ''}`}
              />
            </div>
            <div>
              <label className={labelClasses}>Last Name</label>
              <input
                type="text"
                name="profile.lastName"
                value={formData.profile.lastName}
                onChange={handleChange}
                className={`${inputClasses} ${errors.lastName ? 'border-red-500 ring-1 ring-red-500' : ''}`}
              />
            </div>
          </div>

          <div>
            <label className={labelClasses}>Phone</label>
            <input
              type="tel"
              name="profile.phone"
              value={formData.profile.phone}
              onChange={handleChange}
              className={`${inputClasses} ${errors.phone ? 'border-red-500 ring-1 ring-red-500' : ''}`}
            />
          </div>

          <div>
            <label className={labelClasses}>Role</label>
            {rolesLoading ? (
              <LoadingSpinner size="small" />
            ) : (
              <select
                name="profile.role"
                value={formData.profile.role}
                onChange={handleChange}
                className={selectClasses}
              >
                {visibleRoles.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Company Details</h3>
            <div>
              <label className={labelClasses}>Company Name</label>
              <input
                type="text"
                name="profile.company.name"
                value={formData.profile.company.name}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>Position</label>
              <input
                type="text"
                name="profile.company.position"
                value={formData.profile.company.position}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses}>Department</label>
              <input
                type="text"
                name="profile.company.department"
                value={formData.profile.company.department}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className={`px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md ${
                loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`relative px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md ${
                loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
              }`}
            >
              {loading && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <LoadingSpinner size="small" />
                </span>
              )}
              <span className={loading ? 'invisible' : ''}>
                {loading ? 'Inviting...' : 'Send Invitation'}
              </span>
            </button>
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default AddUserDialog; 